// UserSlice.ts
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import axios from "axios";
import 'react-toastify/dist/ReactToastify.css';
import {BASE_URL} from "../config";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

interface UserState {
    loading: boolean;
    user: any | null;
    error: string | null;
    isLoggedIn: boolean;
    data: HealthCondition[] | null;
}

interface UserCredentials {
    email: string;
    password: string;
    rememberMe: boolean;
}

const initialState: UserState = {
    loading: false,
    user: null,
    error: null,
    isLoggedIn: false,
    data: null,
};

export interface HealthCondition {
    id: string;
    name: string;
    // ... other properties
}

interface UserPayload {
  fullName: string;
  email: string;
  password: string;
  isAdmin: boolean; 
}

export const fetchHealthData = createAsyncThunk('user/fetchHealthData', async (_, thunkAPI) => {
    try {
        
        const response = await fetch(`${BASE_URL}/api/v1/healthConditions/all`); // Replace with your API endpoint
        const result = await response.json();  
        return result.data.healthConditions;
           
    } catch (error: any) {
        // Handle fetch or parsing error
        return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
});

export const fetchUserData = createAsyncThunk('user/fetchUserData', async (_, thunkAPI) => {

    try {
        const responseData = localStorage.getItem('loginData');
        if (responseData !== null) {
            try {
                const parsedData = JSON.parse(responseData);    

                // Assuming your JSON structure matches the previous example
                const accessToken = parsedData?.accessToken;

                if (accessToken) {
                    const response = await fetch(`${BASE_URL}/api/v1/healthConditions`, {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            // Add other headers if needed
                        },
                    });
                    if (!response.ok) {
                        // Handle non-successful response (e.g., 404, 500, etc.)
                        throw new Error(`Request failed with status ${response.status}`);
                    }

                    const jsonData = await response.json();                                
                    return jsonData.data;
                } else {
                    console.error('Access token not found in the response.');
                }
            } catch (error) {
                console.error('Error parsing response data:', error);
            }
        } else {
            console.error('Response data is null.');
        }

        return {}; // or any default value if needed
    } catch (error: any) {
        // Handle fetch or parsing error
        return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
});

export const loginUser = createAsyncThunk<HealthCondition, UserCredentials, { rejectValue: string }>(
    'user/loginUser',
    async (userCredentials: UserCredentials, thunkAPI) => {
        try {
            const response = await axios.post(`${BASE_URL}/api/v1/auth/login`, userCredentials);
            localStorage.setItem('loginData', JSON.stringify(response.data.data));
            localStorage.setItem('isLoggedIn', 'true');
            return response.data.data;
        } catch (error: any) {
            
            toast.error(error.response.data.error.message);
            return thunkAPI.rejectWithValue(error.response.data.error.message);       
            
        }
    }
);


export const registerUser = createAsyncThunk<
  boolean,  // Change the return type to boolean
  UserPayload,
  { rejectValue: string }
>("user/registerUser", async (userPayload: UserPayload, thunkAPI) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/v1/auth/register`,
      userPayload
    );
    
    localStorage.setItem("loginData", JSON.stringify(response.data));
    localStorage.setItem("isLoggedIn", "true");

    return response.data.status;  // Return the status
  } catch (error: any) {
    toast.error(error.response.data.error.message);
    return thunkAPI.rejectWithValue(error.message);
  }
});


export const ChangeUserPassword = createAsyncThunk<
  boolean,  // Change the return type to boolean
  { newPassword: string; password: string; confirmPassword: string }, // Update payload type
  { rejectValue: string }
>("user/ChangeUserPassword", async (userPayload, thunkAPI) => {
  try {
    const responseData = localStorage.getItem('loginData');
    if (responseData !== null) {
        const parsedData = JSON.parse(responseData);

        // Assuming your JSON structure matches the previous example
        const accessToken = parsedData?.accessToken;

        if (!accessToken) {
            throw new Error('Access token not found');
        }

    const response = await axios.post(
      `${BASE_URL}/api/v1/patients/profile/reset-password`,userPayload, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    }     
    );  
    toast('Password reset successful');
    return response.data.status;  // Return the status
    
    }  else {
        console.error('Access token not found in the response.');
    }
  } catch (error: any) {
    toast.error(error.response.data.error.message);
    return thunkAPI.rejectWithValue(error.message);
  }
});


export const logoutUsers = createAsyncThunk(
    'user/logoutUser',
    async (_, {rejectWithValue}) => {
        try {
            const responseData = localStorage.getItem('loginData');
            if (responseData !== null) {
                const parsedData = JSON.parse(responseData);

                // Assuming your JSON structure matches the previous example
                const accessToken = parsedData?.accessToken;

                if (!accessToken) {
                    throw new Error('Access token not found');
                }

                localStorage.removeItem('loginData');
                localStorage.removeItem('isLoggedIn');
                localStorage.removeItem('SetSlug');
                localStorage.removeItem('profile');
                

                const response = await axios.post(`${BASE_URL}/api/v1/auth/logout`, {}, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
               
            }

        } catch (error: any) {
            // Handle error
            return rejectWithValue(error.message);
        }
    }
);

export const deleteHealthCondition = createAsyncThunk(
    'user/deleteHealthCondition',
    async (healthConditionId: string, {getState, rejectWithValue}) => {

        try {
            const responseData = localStorage.getItem('loginData');
            if (responseData !== null) {
                const parsedData = JSON.parse(responseData);

                // Assuming your JSON structure matches the previous example
                const accessToken = parsedData?.accessToken;


                if (!accessToken) {
                    throw new Error('Access token not found');
                }

                // Make API request to delete health condition
                await axios.delete(
                    `${BASE_URL}/api/v1/healthConditions/${healthConditionId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        }
                    }
                );
            }
        } catch (error: any) {
            // Return the error using rejectWithValue
            return rejectWithValue(error.message);
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        logoutUser: (state) => {
            state.isLoggedIn = false;
            state.user = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(registerUser.pending, (state) => {
          state.loading = true;
          state.user = null;
          state.error = null;
        })
        .addCase(registerUser.fulfilled, (state, action) => {
          state.loading = false;
          state.user = action.payload;
          state.error = null;
        })
        .addCase(registerUser.rejected, (state, action) => {
          state.loading = false;
          state.user = null;
          state.error = action.error?.message || 'An error occurred during registration.';
        })
        .addCase(ChangeUserPassword.pending, (state) => {
            state.loading = true;
            state.user = null;
            state.error = null;
          })
          .addCase(ChangeUserPassword.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload;
            state.error = null;
          })
          .addCase(ChangeUserPassword.rejected, (state, action) => {
            state.loading = false;
            state.user = null;
            state.error = action.error?.message || 'An error occurred during registration.';
          })
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
                state.user = null;
                state.error = null;
                state.isLoggedIn = false;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.error = null;
                state.isLoggedIn = true;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.user = null;
                state.isLoggedIn = false;
                if (action.error?.message === "Request failed with status code 422") {
                    state.error = 'Access Denied! Invalid Credentials';
                } else {
                    state.error = action.error?.message || 'An error occurred during login.';
                }
            }).addCase(fetchUserData.pending, (state) => {
            state.loading = true;
        })
            .addCase(fetchUserData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchUserData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'An error occurred';
            }).addCase(deleteHealthCondition.pending, (state) => {
            state.loading = true;
        }).addCase(deleteHealthCondition.fulfilled, (state, action) => {
            state.loading = false;

            // Ensure that state.data is an array before filtering
            if (Array.isArray(state.data)) {
                // Remove the deleted health condition from the data array
                const deletedId = action.payload;
                state.data = state.data.filter((healthCondition) => healthCondition.id !== deletedId);
            }
        }).addCase(deleteHealthCondition.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        }).addCase(fetchHealthData.pending, (state) => {
            state.loading = true;
        })
            .addCase(fetchHealthData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchHealthData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'An error occurred';
            })
        
        ;
    },
});

export const {logoutUser} = userSlice.actions;
export default userSlice.reducer;
